import { useState, useEffect, useCallback, useRef } from 'react';
import {
  ArrowUp, ChevronDown, ChevronRight, ExternalLink,
  CheckCircle2, ArrowRight, Plus, AlertCircle, Pencil,
} from 'lucide-react';
import { DocumentModal } from '@puglieseweb/shared-react-ui';
import { Spinner, LoadingState } from '@systemdox/ui';
import { githubApi } from '@/services/githubApi';
import {
  planningApi,
  type GeneratedPlan,
  type PlanEpic,
  type PlanIssue,
  type PlanningProject,
} from '@/services/planningApi';
import { techStackApi } from '@/services/techStackApi';
import { guardrailsApi } from '@/services/guardrailsApi';
import { orgContextApi } from '@/services/orgContextApi';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { useNotifications } from '@puglieseweb/shared-react-notifications';
import type { PlanHistoryEntry } from '@/services/planHistoryStore';
import { ShapeBriefPanel } from '@/components/changes/ShapeBriefPanel';

interface PlanningStepProps {
  onBoardPopulated: (projectNumber: number) => void;
  onPlanGenerated?: (description: string, plan: GeneratedPlan) => void;
  restoredEntry?: PlanHistoryEntry | null;
}

const STORAGE_KEY = 'planning-step-state';

function loadPersistedState() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as {
      description: string;
      plan: GeneratedPlan | null;
      newProjectTitle: string;
    };
  } catch {
    return null;
  }
}

/** Render a plan issue as one markdown document for the review modal,
 *  mirroring the GitHub issue body the backend composes on populate. */
export function issueToMarkdown(issue: PlanIssue): string {
  const parts = [`# ${issue.title}`, '', issue.description.trim()];
  if (issue.dependencies.length > 0) {
    parts.push('', '### Dependencies', '', ...issue.dependencies.map((d) => `- ${d}`));
  }
  if (issue.acceptanceCriteria.length > 0) {
    parts.push('', '### Acceptance Criteria', '', ...issue.acceptanceCriteria.map((c) => `- [ ] ${c}`));
  }
  return parts.join('\n');
}

/** Parse the modal markdown back into the structured issue. The leading H1
 *  is the title (kept from `base` if removed); Dependencies / Acceptance
 *  Criteria sections map back to their lists; everything else is the
 *  description. Labels are not part of the document and pass through. */
export function markdownToIssue(markdown: string, base: PlanIssue): PlanIssue {
  const lines = markdown.split('\n');
  let title = base.title;
  let i = 0;
  while (i < lines.length && !lines[i].trim()) i++;
  const h1 = lines[i]?.match(/^#\s+(.+)$/);
  if (h1) {
    if (h1[1].trim()) title = h1[1].trim();
    i++;
  }

  let section: 'description' | 'dependencies' | 'criteria' = 'description';
  const description: string[] = [];
  const dependencies: string[] = [];
  const acceptanceCriteria: string[] = [];
  for (; i < lines.length; i++) {
    const line = lines[i];
    const heading = line.match(/^#{2,}\s*(.+?)\s*$/);
    if (heading) {
      const name = heading[1].toLowerCase();
      if (name === 'dependencies') { section = 'dependencies'; continue; }
      if (name === 'acceptance criteria') { section = 'criteria'; continue; }
    }
    if (section === 'description') {
      description.push(line);
    } else {
      const item = line.match(/^\s*[-*]\s*(?:\[[ xX]\]\s*)?(.*)$/);
      if (item && item[1].trim()) {
        (section === 'dependencies' ? dependencies : acceptanceCriteria).push(item[1].trim());
      }
    }
  }
  return { ...base, title, description: description.join('\n').trim(), dependencies, acceptanceCriteria };
}

export function PlanningStep({ onBoardPopulated, onPlanGenerated, restoredEntry }: PlanningStepProps) {
  const { selectedOrg, selectedRepo: workspaceRepo } = useWorkspace();
  const { addNotification } = useNotifications();
  const persisted = loadPersistedState();
  const [description, setDescription] = useState(persisted?.description ?? '');
  const [generating, setGenerating] = useState(false);
  const [plan, setPlan] = useState<GeneratedPlan | null>(persisted?.plan ?? null);
  const [error, setError] = useState<string | null>(null);

  const [repos, setRepos] = useState<string[]>([]);
  // Per-issue repo assignments: { "epicName:issueIndex": "owner/repo" }
  const [issueRepoMap, setIssueRepoMap] = useState<Record<string, string>>({});
  const [projects, setProjects] = useState<PlanningProject[]>([]);
  const [selectedProject, setSelectedProject] = useState<number | null>(null);
  const [newProjectTitle, setNewProjectTitle] = useState(persisted?.newProjectTitle ?? '');

  const [populating, setPopulating] = useState(false);
  const [populateResult, setPopulateResult] = useState<{
    issuesCreated: number;
    projectNumber: number;
    /** Board URL from the backend (create/list), else derived from the
     *  workspace org; null only when neither is known. */
    projectUrl: string | null;
  } | null>(null);
  const [expandedEpics, setExpandedEpics] = useState<Set<string>>(
    () => new Set(persisted?.plan?.epics.map((e) => e.name) ?? []),
  );

  // Review/edit a single plan item in the document modal before populating
  const [openIssue, setOpenIssue] = useState<{ epicName: string; index: number } | null>(null);
  const [modalContent, setModalContent] = useState('');
  const [modalDirty, setModalDirty] = useState(false);
  const applyTimerRef = useRef<number | null>(null);

  // Build default repo map for all issues in a plan
  const buildDefaultRepoMap = useCallback((epics: PlanEpic[], defaultRepo: string) => {
    const map: Record<string, string> = {};
    epics.forEach((epic) => {
      epic.issues.forEach((_, i) => {
        map[`${epic.name}:${i}`] = defaultRepo;
      });
    });
    return map;
  }, []);

  // Restore a plan from history, or reset when cleared
  useEffect(() => {
    setOpenIssue(null);
    if (restoredEntry) {
      setDescription(restoredEntry.description);
      setPlan(restoredEntry.plan);
      setNewProjectTitle(restoredEntry.plan.productName || '');
      setExpandedEpics(new Set(restoredEntry.plan.epics.map((e) => e.name)));
      setPopulateResult(null);
      setError(null);
      if (repos.length > 0) {
        setIssueRepoMap(buildDefaultRepoMap(restoredEntry.plan.epics, repos[0]));
      }
    } else if (restoredEntry === null) {
      setDescription('');
      setPlan(null);
      setNewProjectTitle('');
      setExpandedEpics(new Set());
      setPopulateResult(null);
      setError(null);
      setIssueRepoMap({});
      sessionStorage.removeItem(STORAGE_KEY);
    }
  }, [restoredEntry]);

  // Persist plan state to sessionStorage
  useEffect(() => {
    if (plan || description) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ description, plan, newProjectTitle }));
    } else {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  }, [description, plan, newProjectTitle]);

  useEffect(() => {
    githubApi.listRepos().then((list) => {
      const connected = list
        .filter((r) => r.connected)
        .filter((r) => !selectedOrg || r.owner === selectedOrg)
        .map((r) => r.full_name);
      setRepos(connected);
      // If we have a plan but no repo assignments yet, initialise them
      if (plan && connected.length > 0 && Object.keys(issueRepoMap).length === 0) {
        setIssueRepoMap(buildDefaultRepoMap(plan.epics, connected[0]));
      }
    });
    planningApi.listProjects().then((data) => {
      setProjects(data.filter((p) => !p.closed));
    });
  }, [selectedOrg]);

  const handleGenerate = useCallback(async () => {
    if (!description.trim() || generating) return;
    setGenerating(true);
    setPlan(null);
    setError(null);
    setPopulateResult(null);
    try {
      // Auto-sync context if stale (>24h) or missing new fields — ensures CLAUDE.md snippets are fresh
      try {
        const orgRepos = await orgContextApi.listRepos();
        const now = Date.now();
        const staleThreshold = 24 * 60 * 60 * 1000; // 24 hours
        const hasStale = orgRepos.some((r) => {
          if (!r.connected) return false;
          const ctx = r.context;
          if (!ctx?.updatedAt) return true; // never synced
          if (!ctx.existingServices) return true; // synced with old schema
          return now - new Date(ctx.updatedAt).getTime() > staleThreshold;
        });
        if (hasStale) {
          const { jobId } = await orgContextApi.syncAll();
          // Poll until sync completes (max 60s)
          for (let i = 0; i < 20; i++) {
            await new Promise((r) => setTimeout(r, 3000));
            const status = await orgContextApi.getSyncJobStatus(jobId);
            if (status.status === 'completed' || status.status === 'failed') break;
          }
        }
      } catch {
        // Sync failure is non-blocking — proceed with whatever context is available
      }

      // Fetch Define context + org context in parallel
      // Pass the workspace repo so the backend includes its full CLAUDE.md
      const [techStack, guardrails, planningContext] = await Promise.all([
        techStackApi.list().catch(() => []),
        guardrailsApi.listEntries().catch(() => []),
        orgContextApi.getPlanningContext(workspaceRepo || undefined).catch(() => ({ repoSummaries: [], dependencies: [], adrs: [], specs: [] })),
      ]);

      const context = {
        techStack: techStack.map((t) => ({ name: t.name, version: t.version, category: t.category, notes: t.notes })),
        guardrails: guardrails.map((g) => ({ title: g.title, description: g.description, category: g.category, severity: g.severity })),
        org: selectedOrg || undefined,
        repo: workspaceRepo || undefined,
        planningContext,
      };

      const result = await planningApi.generatePlan(description.trim(), context);
      setPlan(result);
      // Auto-expand all epics
      const epics = new Set(result.epics.map((e) => e.name));
      setExpandedEpics(epics);
      // Set new project title from plan
      if (result.productName) {
        setNewProjectTitle(result.productName);
      }
      // Initialise per-issue repo assignments
      if (repos.length > 0) {
        setIssueRepoMap(buildDefaultRepoMap(result.epics, repos[0]));
      }
      // Save to history
      onPlanGenerated?.(description.trim(), result);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Plan generation failed';
      setError(message);
      addNotification('failed', 'Plan generation failed', message);
    } finally {
      setGenerating(false);
    }
  }, [description, generating, addNotification, onPlanGenerated]);

  const handlePopulateBoard = useCallback(async () => {
    if (!plan || populating) return;

    const projectNumber = selectedProject;
    if (!projectNumber && !newProjectTitle.trim()) return;

    setPopulating(true);
    try {
      // Create project if needed
      let targetProjectNumber = projectNumber;
      let projectUrl: string | null = null;
      if (!targetProjectNumber && newProjectTitle.trim()) {
        const created = await planningApi.createProject(newProjectTitle.trim());
        targetProjectNumber = created.number;
        projectUrl = created.url ?? null;
        setSelectedProject(targetProjectNumber);
      }

      if (!targetProjectNumber) {
        setError('Could not determine target project');
        return;
      }

      // The board URL comes from the backend (the created project, or the
      // listed one the user picked); as a last resort derive it from the
      // workspace org — never a hardcoded organisation.
      if (!projectUrl) {
        projectUrl =
          projects.find((p) => p.number === targetProjectNumber)?.url ??
          (selectedOrg
            ? `https://github.com/orgs/${selectedOrg}/projects/${targetProjectNumber}`
            : null);
      }

      // Group issues by target repo, preserving epic structure
      const repoEpicsMap = new Map<string, PlanEpic[]>();
      for (const epic of plan.epics) {
        const issuesByRepo = new Map<string, typeof epic.issues>();
        epic.issues.forEach((issue, i) => {
          const repo = issueRepoMap[`${epic.name}:${i}`] || repos[0];
          if (!issuesByRepo.has(repo)) issuesByRepo.set(repo, []);
          issuesByRepo.get(repo)!.push(issue);
        });
        for (const [repo, issues] of issuesByRepo) {
          if (!repoEpicsMap.has(repo)) repoEpicsMap.set(repo, []);
          repoEpicsMap.get(repo)!.push({ ...epic, issues });
        }
      }

      // Bulk create issues per repo
      let totalCreated = 0;
      for (const [repo, epics] of repoEpicsMap) {
        const result = await planningApi.bulkCreateIssues(
          String(targetProjectNumber),
          repo,
          epics,
        );
        totalCreated += result.issuesCreated;
      }

      setPopulateResult({
        issuesCreated: totalCreated,
        projectNumber: targetProjectNumber,
        projectUrl,
      });

      addNotification(
        'completed',
        'Board populated',
        `${totalCreated} issues created across ${repoEpicsMap.size} repo(s) on project board.`,
        '/connect',
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to populate board';
      setError(message);
      addNotification('failed', 'Board population failed', message);
    } finally {
      setPopulating(false);
    }
  }, [plan, repos, issueRepoMap, selectedProject, newProjectTitle, populating, addNotification, projects, selectedOrg]);

  const toggleEpic = (name: string) => {
    setExpandedEpics((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  // ── Plan item review modal ──

  const openIssueData = openIssue
    ? plan?.epics.find((e) => e.name === openIssue.epicName)?.issues[openIssue.index] ?? null
    : null;

  const openIssueModal = (epicName: string, index: number, issue: PlanIssue) => {
    setOpenIssue({ epicName, index });
    setModalContent(issueToMarkdown(issue));
    setModalDirty(false);
  };

  const applyModalEdits = useCallback(() => {
    if (!openIssue) return;
    setPlan((prev) => {
      if (!prev) return prev;
      const epic = prev.epics.find((e) => e.name === openIssue.epicName);
      const original = epic?.issues[openIssue.index];
      if (!original) return prev;
      const updated = markdownToIssue(modalContent, original);
      const renamed = updated.title !== original.title;
      return {
        ...prev,
        epics: prev.epics.map((e) => ({
          ...e,
          issues: e.issues.map((iss, i) => {
            if (e.name === openIssue.epicName && i === openIssue.index) return updated;
            // Populate resolves dependencies by exact title — follow a rename.
            if (renamed && iss.dependencies.includes(original.title)) {
              return {
                ...iss,
                dependencies: iss.dependencies.map((d) => (d === original.title ? updated.title : d)),
              };
            }
            return iss;
          }),
        })),
      };
    });
    setModalDirty(false);
  }, [openIssue, modalContent]);

  // Debounced apply while typing — the ref always points at the latest
  // closure so the timer never applies stale content.
  const applyRef = useRef(applyModalEdits);
  useEffect(() => { applyRef.current = applyModalEdits; }, [applyModalEdits]);

  const handleModalContentChange = useCallback((content: string) => {
    setModalContent(content);
    setModalDirty(true);
    if (applyTimerRef.current) window.clearTimeout(applyTimerRef.current);
    applyTimerRef.current = window.setTimeout(() => {
      applyTimerRef.current = null;
      applyRef.current();
    }, 600);
  }, []);

  const closeIssueModal = useCallback(() => {
    if (applyTimerRef.current) {
      window.clearTimeout(applyTimerRef.current);
      applyTimerRef.current = null;
    }
    applyRef.current();
    setOpenIssue(null);
  }, []);

  useEffect(() => () => {
    if (applyTimerRef.current) window.clearTimeout(applyTimerRef.current);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleGenerate();
    }
  };

  // Success state — board populated
  if (populateResult) {
    return (
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="text-center space-y-4 max-w-md">
          <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto" />
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            Board Populated
          </h3>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            {populateResult.issuesCreated} issues created on the project board.
          </p>
          <div className="flex items-center gap-3">
            <button
              onClick={() => onBoardPopulated(populateResult.projectNumber)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors"
            >
              Go to Connect
              <ArrowRight className="w-4 h-4" />
            </button>
            {populateResult.projectUrl && (
              <a
                href={populateResult.projectUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
              >
                <ExternalLink className="w-4 h-4" />
                View on GitHub
              </a>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Empty state — input centered */}
      {!plan && !generating && !error && (
        <div className="flex-1 flex flex-col items-center justify-center p-4 gap-4 max-w-2xl mx-auto w-full">
          <p className="text-sm text-slate-400">
            Describe what you want to build and generate a plan.
          </p>
          <div className="w-full relative">
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Describe the product or feature you want to plan... (Shift+Enter for new line)"
              disabled={generating}
              rows={3}
              className="w-full px-4 py-3 pr-12 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            />
            <button
              onClick={handleGenerate}
              disabled={!description.trim() || generating}
              className="absolute right-3 bottom-3 p-2 rounded-full bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ArrowUp className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Generating state */}
      {generating && (
        <LoadingState size="lg" label="Generating plan..." className="flex-1" />
      )}

      {/* Plan preview area */}
      {(plan || (error && !generating)) && (
      <div className="flex-1 overflow-y-auto p-4">

        {error && !generating && (
          <div className="flex flex-col items-center justify-center h-full gap-3 max-w-md mx-auto text-center">
            <AlertCircle className="w-8 h-8 text-red-500" />
            <p className="text-sm font-medium text-red-600 dark:text-red-400">
              Plan generation failed
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400 whitespace-pre-wrap">
              {error}
            </p>
            <button
              onClick={() => setError(null)}
              className="text-xs text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
            >
              Dismiss and try again
            </button>
          </div>
        )}

        {plan && (
          <div className="space-y-3 max-w-3xl mx-auto">
            {description && (
              <p className="text-xs text-slate-500 dark:text-slate-400 italic border-l-2 border-slate-200 dark:border-slate-700 pl-3">
                {description}
              </p>
            )}
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                {plan.productName}
              </h3>
              <span className="text-xs text-slate-400">
                {plan.epics.length} epics &middot; {plan.epics.reduce((sum, e) => sum + e.issues.length, 0)} issues
              </span>
            </div>

            {plan.epics.map((epic) => (
              <EpicCard
                key={epic.name}
                epic={epic}
                expanded={expandedEpics.has(epic.name)}
                onToggle={() => toggleEpic(epic.name)}
                repos={repos}
                issueRepos={Object.fromEntries(
                  epic.issues.map((_, i) => [i, issueRepoMap[`${epic.name}:${i}`] ?? repos[0] ?? '']),
                )}
                onIssueRepoChange={(issueIndex, repo) => {
                  setIssueRepoMap((prev) => ({ ...prev, [`${epic.name}:${issueIndex}`]: repo }));
                }}
                onOpenIssue={(issueIndex, issue) => openIssueModal(epic.name, issueIndex, issue)}
              />
            ))}
          </div>
        )}
      </div>
      )}

      {/* Plan item review/edit modal — open before populating the board */}
      {openIssue && openIssueData && (
        <DocumentModal
          item={{
            id: `${openIssue.epicName}:${openIssue.index}`,
            title: openIssueData.title,
            subtitle: `${openIssue.epicName} · ${issueRepoMap[`${openIssue.epicName}:${openIssue.index}`] || repos[0] || 'No repo'}`,
            content: modalContent,
          }}
          editedContent={modalContent}
          hasUnsavedChanges={modalDirty}
          onClose={closeIssueModal}
          onContentChange={handleModalContentChange}
          onSave={applyModalEdits}
          renderFooter={() => (
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Edits apply to the plan draft automatically — the first heading is the issue title.
              Nothing is created on GitHub until you populate the board.
            </p>
          )}
        />
      )}

      {/* Plan footer — project board + populate */}
      {plan && (
        <div className="border-t border-slate-200 dark:border-slate-800 p-4 space-y-3">
          {/* The brief step (ADR-201 Amendment 2 point 2): a plan draft is the
              intake of a change; the brief's four parts are what the owner
              approves, and the board follows from plan.md. A preview behind the
              "Functionality available soon" banner — Populate Board below
              stays as it is. */}
          <ShapeBriefPanel
            productName={plan.productName}
            epics={plan.epics.length}
            stories={plan.epics.reduce((sum, e) => sum + e.issues.length, 0)}
          />
          <div className="flex gap-3 items-end">
            <div className="flex-1">
              <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1 block">
                Project Board
              </label>
              <select
                value={selectedProject ?? 'new'}
                onChange={(e) => {
                  const val = e.target.value;
                  setSelectedProject(val === 'new' ? null : Number(val));
                }}
                className="w-full px-2.5 py-1.5 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-xs"
              >
                <option value="new">+ Create new project</option>
                {projects.map((p) => (
                  <option key={p.number} value={p.number}>{p.title}</option>
                ))}
              </select>
            </div>
            {selectedProject === null && (
              <div className="flex-1">
                <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1 block">
                  Project Name
                </label>
                <input
                  value={newProjectTitle}
                  onChange={(e) => setNewProjectTitle(e.target.value)}
                  className="w-full px-2.5 py-1.5 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-xs"
                  placeholder="Project name..."
                />
              </div>
            )}
            <button
              onClick={handlePopulateBoard}
              disabled={populating || (!selectedProject && !newProjectTitle.trim()) || repos.length === 0}
              className="flex items-center gap-1.5 px-4 py-1.5 rounded-md bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors shrink-0"
            >
              {populating ? (
                <Spinner size="sm" tone="current" />
              ) : (
                <Plus className="w-3.5 h-3.5" />
              )}
              Populate Board
            </button>
          </div>
          <button
            onClick={() => {
              setPlan(null);
              setDescription('');
              setNewProjectTitle('');
              setPopulateResult(null);
              setIssueRepoMap({});
              sessionStorage.removeItem(STORAGE_KEY);
            }}
            className="text-xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
          >
            Start over with a new plan
          </button>
        </div>
      )}
    </div>
  );
}

interface EpicCardProps {
  epic: PlanEpic;
  expanded: boolean;
  onToggle: () => void;
  repos: string[];
  issueRepos: Record<number, string>;
  onIssueRepoChange: (issueIndex: number, repo: string) => void;
  onOpenIssue: (issueIndex: number, issue: PlanIssue) => void;
}

function EpicCard({ epic, expanded, onToggle, repos, issueRepos, onIssueRepoChange, onOpenIssue }: EpicCardProps) {
  return (
    <div className="rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
      >
        {expanded ? (
          <ChevronDown className="w-3.5 h-3.5 text-slate-400 shrink-0" />
        ) : (
          <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />
        )}
        <span className="text-xs font-medium text-slate-700 dark:text-slate-300 flex-1">
          {epic.name}
        </span>
        <span className="text-[10px] text-slate-400 px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800">
          {epic.phase}
        </span>
        <span className="text-[10px] text-slate-400">
          {epic.issues.length} issues
        </span>
      </button>

      {expanded && (
        <div className="border-t border-slate-100 dark:border-slate-800">
          {epic.description && (
            <p className="px-3 py-1.5 text-[11px] text-slate-500 dark:text-slate-400">
              {epic.description}
            </p>
          )}
          {epic.issues.map((issue, i) => (
            // A div, not a <button>: the row hosts its own repo <select>,
            // and interactive elements must not nest.
            <div
              key={i}
              role="button"
              tabIndex={0}
              onClick={() => onOpenIssue(i, issue)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onOpenIssue(i, issue);
                }
              }}
              title="Review and edit this item"
              className="group flex items-start gap-2 px-3 py-1.5 border-t border-slate-50 dark:border-slate-800/50 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
            >
              <span className="text-[10px] text-slate-400 mt-0.5 shrink-0">
                {i + 1}.
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-xs text-slate-700 dark:text-slate-300 flex-1">
                    {issue.title}
                  </p>
                  <Pencil className="w-3 h-3 text-slate-300 dark:text-slate-600 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                  <select
                    value={issueRepos[i] ?? ''}
                    onChange={(e) => onIssueRepoChange(i, e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    className="text-[10px] px-1.5 py-0.5 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-500 dark:text-slate-400 shrink-0 max-w-[180px]"
                  >
                    {repos.map((r) => (
                      <option key={r} value={r}>{r.split('/')[1]}</option>
                    ))}
                  </select>
                </div>
                {issue.labels.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-0.5">
                    {issue.labels.map((label) => (
                      <span
                        key={label}
                        className="text-[9px] px-1 py-0 rounded bg-slate-100 dark:bg-slate-800 text-slate-500"
                      >
                        {label}
                      </span>
                    ))}
                  </div>
                )}
                {issue.dependencies.length > 0 && (
                  <p className="text-[10px] text-slate-400 mt-0.5">
                    Depends on: {issue.dependencies.join(', ')}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
